/* Dynamic Plot code copyright 2015 (c) Tuva Labs Inc. */
/**
 * created by jpatel
 * DynMiscellany.js -- global controls for the Dynamic plotting code.
 */

//import * as Sentry from "@sentry/react";
import Mediator from "mediator-js";

import '../raphael-extensions/inline_text_editing';
import '../raphael-extensions/line';

import DynG from '../../utils/Global';

import { wrapDynDataSet } from '../dynDataSet/dataSet.js';
import { wrapDynCaseFilter } from '../dynDataSet/caseFilter.js';
import { wrapDynCaseSampler } from '../dynDataSet/caseSampler.js';
import { wrapDynAttribute } from '../dynDataSet/attribute.js';
import { wrapDynFormula } from '../dynDataSet/formula.js';
import { wrapDynmathUtil } from '../dynDataSet/mathUtil.js';
import { wrapDyndataUtil } from '../dynDataSet/dataUtil.js';
import { wrapDyncolorUtil } from '../dynDataSet/colorUtil.js';
import { wrapDynDataSonification } from '../dynDataSet/dataSonification.js';
import { wrapDynplotUtil } from '../dynplot/plotUtil.js';
import { wrapDynPlotLabels } from '../dynplot/plotLabels.js';
import { wrapDynTopMarginView } from '../dynplot/topMarginView.js';
import { wrapDynRightMarginView } from '../dynplot/rightMarginView.js';
import { wrapDynaxisUtil } from '../dynplot/axisUtil.js';
import { wrapDynAxisView } from '../dynplot/axisView.js';
import { wrapDynPlotElement } from '../dynplot/plotElement/plotElement.js';
import { wrapDynDotPlotElement } from '../dynplot/plotElement/dotPlotElement.js';
import { wrapDynBoxPlotElement } from '../dynplot/plotElement/boxPlotElement.js';
import { wrapDynPieElement } from '../dynplot/plotElement/pieElement.js';
import { wrapDynBarElement } from '../dynplot/plotElement/barElement.js';
import { wrapDynHistoElement } from '../dynplot/plotElement/histoElement.js';
import { wrapDynPlotShaper } from '../dynplot/plotShaper.js';
import { wrapDynPlotMeasures } from '../dynplot/plotMeasures.js';
import { wrapDynIconStyler } from '../dynplot/iconStyler.js';
import { wrapDynaverageElement } from '../dynplot/plotElement/averageElement.js';
import { wrapDynSpreadElement } from '../dynplot/plotElement/spreadElement.js';
import { wrapDynSquaresOnLineModel } from '../dynplot/plotElement/SquaresOnLineModel.js';
import { wrapDynRefLineElement } from '../dynplot/plotElement/refLineElement.js';
import { wrapDynLeastSquaresElement } from '../dynplot/plotElement/leastSquaresElement.js';
import { wrapDynPlotFunctionElement } from '../dynplot/plotElement/plotFunctionElement.js';
import { wrapDynAnnotateElement } from '../dynplot/plotElement/annotateElement.js';
import { wrapDynMapElement } from '../dynplot/plotElement/mapElement.js';
import { wrapDynCountElement } from '../dynplot/plotElement/countElement.js';
import { wrapDynGridLinesElement } from '../dynplot/plotElement/gridLinesElement.js';
import { wrapDynProgressIndicator } from '../dynplot/progressIndicator.js';
import { wrapDynPlotView } from '../dynplot/plotView.js';
import { wrapDynToolTip } from '../dynplot/toolTip.js';
import { wrapDynSampleStats } from '../dynplot/sampleStats.js';
import { wrapDynPreferences } from '../dynDataSet/dynPreferences.js';
import { func } from "prop-types";
import { wrapDynMixupElement } from "../dynplot/plotElement/mixupElement";
import { UndoStep, UndoHelper, UndoManager } from "./undoManager";
import { wrapRulerElement } from '../dynplot/plotElement/rulerElement.js';

/**
 * Dyn : Global object to hold dynamic plot environment (objects, functions, data)
 */

export const initDyn = function(isAuthoring) {
let debugLevel = Number((new URLSearchParams(window.location.search)).get('debug')) || 0;
let Dyn = {

	mediator : new Mediator(),
    isAuthoring: isAuthoring,

	debugging : (debugLevel>=1),
	debugLogAssertFail : (debugLevel>=1),
	debugStopOnAssertFail : (debugLevel>=2),
    debugTranslations: (debugLevel>=1), // show missing translation in console?
    debugSVG : (debugLevel>=4),
    //debugTouch : (debugLevel>=2),
    debugUnitTestShowGrandSummary : (debugLevel>=1), // run and show a single summary line for all unit tests
    debugUnitTestShowModules : (debugLevel>=3), // run and show per-module failures+successes summary line
    debugUnitTestStopOnTestFail : (debugLevel>=4), // stop on test failure
    debugShowEachUnitTest : (debugLevel>=5), // show each unit test pass/fail
    debugDataInput: (debugLevel>=3), // show console messages for data input and conversions?
    debugViewRects : (debugLevel>=5), // show view rectangle outlines? see DynG.toggleDebugViewRects()
    debugUndo: (debugLevel>=3),
    debugMathJax: (debugLevel>=3),
    debugCounts: (debugLevel>=4),
    debugPlotUpdates: (debugLevel>=4),

	environment: null,

	// objects
	dataSet : null,	// table data as attributes and cases
	plotView : null,
	toolTip : null,
	preferences : null,
	subscribersToClean: [],
	// Add subscribers used inside Dyn library to clean it later
	addSubcribersToClean: function(subscriber) {
		this.subscribersToClean.push({
			namespace: subscriber.channel.namespace,
			id: subscriber.id,
		})
	},
	// To clean the subscribers used inside Dyn library
	cleanMeditorSubscribers: function() {
		this.subscribersToClean.forEach(subscriber => {
			this.mediator.remove(subscriber.namespace, subscriber.id);
		});
		this.subscribersToClean = [];
	},
    // subscribe to one mediator channel and save subscription to localArray
    // used to add then remove subscriptions as objects are created then destroyed
    // without interfering with other subscriptions to the same channel.
    mediatorLocalSubscribe: function( localArray, namespace, callback ) {
        let subscriber = this.mediator.subscribe( namespace, callback );
        Dyn.assertValidArray( localArray );
        localArray.push({
            namespace: subscriber.channel.namespace,
            id: subscriber.id,
        });
    },
    // unsubscribe from one mediator channel added to local array, if it exists
    mediatorLocalRemove: function( localArray, namespace ) {
        Dyn.assertValidArray( localArray );
	    let index = localArray.findIndex( s => s.namespace === namespace );
        if( index >= 0 ) {
            let subscription = localArray[index];
            this.mediator.remove( subscription.namespace, subscription.id );
            localArray.slice( index, 1 );
        }
    },
    // unsubscribe from all mediator channels in local array
    mediatorLocalRemoveAll: function( localArray ) {
        Dyn.assertValidArray( localArray );
        localArray.forEach( ( subscription ) => {
            this.mediator.remove( subscription.namespace, subscription.id );
        } );
        localArray.length = 0;
    },
};
DynG.toggleDebugViewRects = function() {
    Dyn.debugViewRects = ! Dyn.debugViewRects;
    window.tuvaDataTools.actions.resize(); // refresh either TuvaJr or PlotView
};

/**
 * Environment check for the device
 * Used to handle the mouse and touch events for the mobile device
 */
Dyn.environment = {
    //mobile or desktop compatible event name, to be used with '.on' function
    isAndroid: function() {
        "use strict";
        return navigator.userAgent.match(/Android/i);
    },
    isBlackBerry: function() {
        "use strict";
        return navigator.userAgent.match(/BlackBerry/i);
    },
    isIOS: function() {
        "use strict";
        return navigator.userAgent.match(/iPhone|iPad|iPod/i) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    },
    isOpera: function() {
        "use strict";
        return navigator.userAgent.match(/Opera Mini/i);
    },
    isWindows: function() {
        "use strict";
        return navigator.userAgent.match(/IEMobile/i);
    },
    isMobile: function() {
        "use strict";
        return this.isTouchDevice() || Dyn.environment.isAndroid() || Dyn.environment.isBlackBerry() || Dyn.environment.isIOS() || Dyn.environment.isOpera() || Dyn.environment.isWindows();
    },
    isTouchDevice: function() {
        "use strict";
        //taken from Modernizr touch test
        const isTouchCapable = 'ontouchstart' in window || (window.DocumentTouch && document instanceof window.DocumentTouch) || window.navigator.maxTouchPoints > 0 || window.navigator.msMaxTouchPoints > 0;
        return  isTouchCapable || Dyn.environment.isAndroid() || Dyn.environment.isIOS(); //|| Dyn.debugTouch ;
    },
    /**
     * Detect if device has a fine pointer (mouse, trackpad) in addition to or instead of touch.
     * This is used to enable features like marquee selection on touch-enabled laptops.
     * Uses CSS media query 'pointer: fine' which detects precision pointing devices.
     * @returns {boolean} true if device has a fine pointer input
     */
    hasFinePointer: function() {
        "use strict";
        // Check if matchMedia is supported and if device has a fine pointer
        // 'pointer: fine' matches devices with accurate pointing devices like mouse or trackpad
        // This returns true for laptops with trackpads even if they also have touch screens
        return window.matchMedia && window.matchMedia('(pointer: fine)').matches;
    }
};

// Dyn.preferences =... moved from here to dynPreferences.js
    wrapDynPreferences( Dyn );

/**
 * assert
 *
 * if debugging, use to test assertions and log message if assertion is false
 *
 * does nothing if Dyn.debugg
 *
 * @param expression
 * @param message
 */
Dyn.assert = function( expression, message ) {
	"use strict";
	var valid = Boolean( expression );
	if( Dyn.debugging && !valid ){
		if( Dyn.debugLogAssertFail )
			console.log("assertion failed "+message );
		if( Dyn.debugStopOnAssertFail )
			debugger;
	}
	return valid;
};

Dyn.assertArrayIndex = function( array, index, message ) {
	"use strict";
	var valid = (index >= 0 && index < array.length );
	if( Dyn.debugging )
		Dyn.assert( valid, message || "array index out of bounds" );
	return valid;
};

Dyn.assertFiniteNumeric = function( value, message ) {
	"use strict";
	return Dyn.assert( Dyn.mathUtil.isFinite( value ), message || "not a finite numeric value");
};

Dyn.assertHasProperty = function( object, propertyString ) {
    return Dyn.assert( Object.values( object ).includes( propertyString ),
        "object does not have a property with name:"+propertyString );
};

Dyn.assertHasValue = function( object, value ) {
    if( Array.isArray( object )) {
        return Dyn.assert( object.includes(value),
            "array does not have that value");
    } else if( typeof object === 'object' ) {
        return Dyn.assert( Object.values( object ).includes( value ),
            "object does not have a property with that value");
    }
    return Dyn.assert( false );
}

Dyn.assertValidRect = function( rect, message ) {
	"use strict";
	Dyn.assert( rect, message );
	Dyn.assertFiniteNumeric( rect.x, message );
	Dyn.assertFiniteNumeric( rect.y, message );
	Dyn.assertFiniteNumeric( rect.width, message );
	Dyn.assertFiniteNumeric( rect.height, message );
	Dyn.assert( rect.width >=0 , message );
	Dyn.assert( rect.height >=0 , message );
};

Dyn.assertValidCircle = function ( c, message ) {
	"use strict";
	Dyn.assert( c, message );
	Dyn.assertFiniteNumeric( c.x, message );
	Dyn.assertFiniteNumeric( c.y, message );
	Dyn.assertFiniteNumeric( c.r, message );
};

// is the object a an array?
// because there are multiple ways to check we define a consistent method here
Dyn.assertValidArray = function( a, message ) {
	"use strict";
	Dyn.assert( Array.isArray( a ));
	// or ( a instanceof Array )
	// or ( a.constructor === Array)
	// or ( $.isArray( a ))
};

Dyn.assertNonEmptyString = function( str, message ) {
    Dyn.assert( typeof str === 'string' && str.length > 0, message );
};

// show complexity of SVG elements for optimization metrics
Dyn.logSizeOfSVGDoc = function() {
	"use strict";
    let svgs = document.getElementsByTagName("svg"),
        totalChildren = 0,
        childCounts = [];

    for( let i=0; i<svgs.length; i++ ){
        let svg = svgs[i];
        totalChildren += svg.childElementCount;
        childCounts.push( svg.childElementCount );
    }

    let tags = [ "clipPath", "circle", "rect", "path", "text" ],
        tagMessage = "";
    tags.forEach( tagName => {
        tagMessage += ( document.getElementsByTagName(tagName).length + " ");
    });

    console.log( 'logSizeOfSVGDoc totalChildren: '+ totalChildren, /* SVG childElementCounts:", childCounts,*/ " by tag:", tagMessage + tags );
};

class UnitTester {
    // static consoleWarnings = Dyn.debugRunUnitTests;  // show warnings in console
    static debugUnitTestShowModules = Dyn.debugUnitTestShowModules; //(debugLevel > 2); // show summary of passed tests in console
    static debugUnitTestStopOnTestFail = Dyn.debugUnitTestStopOnTestFail;   // stop on test failure (using Dyn.assert)
    static debugShowEachUnitTest = Dyn.debugShowEachUnitTest; // verbose log if true
    static testAccumulator = {
        passCount:0,
        failCount:0,
        moduleCount:0,
    };

    constructor( moduleName ) {
        this.moduleName = moduleName;
        this.passCount=0;
        this.failCount=0;
    }

    /** count results of one unit test, assert if false and not a warning. */
    test( bool, message /*optional*/, isWarning /*optional*/, dontStop /*optional*/ ) {
        if( bool ) {
            this.passCount++;
            if( UnitTester.debugShowEachUnitTest ) {
                console.log( "passed test", this.passCount, message );
            }
        } else {
            this.failCount++;
            console.warn( isWarning ? "Warning:" : "FAILED test:", message );
            if( UnitTester.debugUnitTestStopOnTestFail && !(isWarning || dontStop)) {
                debugger;
            }
        }
        return bool;
    }
    /** count results of one unit test, sending a console message if test failure */
    testAndWarn( bool, message ) {
        return this.test( bool, message, true );
    }
    /** count results of one unit test, but don't stop or send a warning on failure; only summary will show it as a failure */
    testDontStop( bool, message ) {
        return this.test( bool, message, false, true );
    }
    report() {
        const pad = ( n ) => String(n).padStart( 4, '0');

        UnitTester.testAccumulator.passCount += this.passCount;
        UnitTester.testAccumulator.failCount += this.failCount;
        UnitTester.testAccumulator.moduleCount += 1;

        if( this.failCount ) {
            console.log( "===> FAILED " + this.failCount + " and passed " + this.passCount + " tests for " + this.moduleName );
        } else {
            UnitTester.debugUnitTestShowModules && console.log( "passed " + this.passCount + " tests for " + this.moduleName );
        }
    }
}
Dyn.UnitTester = UnitTester;

setTimeout( () => { // report after all unit tests are run
    if( Dyn.debugUnitTestShowGrandSummary ) {
        let a = UnitTester.testAccumulator;
        console.log( "Unit tests:", a.failCount, "failed,", a.passCount, "passed, in", a.moduleCount, "test modules" );
    }
}, 2000 );

class LocUtil { // localization utilities to help Dyn.loc() do its job.
    static missingTranslations = {}; // no msgid found for given .po file
    static nullTranslations = {}; // msgstr is identical to string to be translated
    static missingMsgIDs = {}; // msgid found in one language but not others
    static bothUandLCaseMsgIDs = {}; // msgID is identical except differing in case only (keysToLowerCase() does not allow this)
    // NOTE: we are unable to test for actual duplicate 'msgid' in .po files, po2json removes them before we can examine
    static unitTestHasRun = false;

    /** fix the locale's msgid keys to be all lower case, to match what Dyn.loc() expects */
    static keysToLowerCase( loc, lang ) {
        //let count=0;
        if( loc && loc.locale_data ) {
            let newBadIds = {},
                messages = loc.locale_data.messages;
            // find and save problematic msgids for this language
            Object.keys( messages ).forEach( id => {
                if( LocUtil.isMsgID(id) && id !== id.toLowerCase() ) {
                    newBadIds[id] = { lang: lang, id: id, m: messages[id] };
                }
            } );
            // now fix them
            Object.values( newBadIds ).forEach( bad => {
                delete messages[bad.id];
                messages[bad.id.toLowerCase()] = bad.m;
                //++count;
            } );
        }
        Dyn.debugTranslations && console.log( "Translations - show accumulated warnings with: window.DynG.default.translations" );
        return loc;
    }

    /** is this parameter a msgid: msgstr pair? Need to week out the empty string parameter, etc. */
    static isMsgID( id ) {
        return(typeof id === 'string' && id.length > 0);
    }

    static unitTest( locale ) {
        let unitTester = new Dyn.UnitTester( "Dyn.locUtil.unitTest()" );
        for (const [lang, langData] of Object.entries(locale)) {
            Object.keys( langData.locale_data.messages ).forEach( id => {
                let msgstr = LocUtil.isMsgID(id) && langData.locale_data.messages[id][0];
                if( msgstr ) {
                    if( ! LocUtil.missingMsgIDs.hasOwnProperty(id )) {
                        LocUtil.unitTestMissingMsgIDs( locale, id, unitTester );
                    }
                    if( id.includes("{{") ) {
                        unitTester.test( id.includes( "}}" ), 'msgid has unbalanced {{ }} : "' + id + '" in '+lang+'.po' );
                    }
                    if( msgstr.includes("{{") ) {
                        unitTester.test( msgstr.includes( "}}" ), 'msgstr has unbalanced {{ }} : "' + msgstr + '" in '+lang+'.po' );
                    }
                }
                if( id !== id.toLowerCase() && langData.locale_data.messages.hasOwnProperty( id.toLowerCase())) {
                    LocUtil.bothUandLCaseMsgIDs[lang+":"+id] = id.toLowerCase();
                    unitTester.testAndWarn( false, 'both upper and lower case language key in '+lang+': "'+id+'", "'+id.toLowerCase()+'"' );
                }
            });
        }
        unitTester.report();
        LocUtil.unitTestHasRun = true;
    }

    /** check other languages to see if they also have the ID that the given language has,
     * If not, record this as an error */
    static unitTestMissingMsgIDs( locale, id, unitTester ) {
        let missing="", present="";
        for (const [lang, langData] of Object.entries(locale)) { // for each language, language data pair
            if( ! langData.locale_data.messages.hasOwnProperty( id )) {
                missing += missing ? (", "+lang) : ("missing in "+lang);
            } else {
                present += present ? (", "+lang) : ("present in "+lang);
            }
        }
        if( missing ) {
            LocUtil.missingMsgIDs[id] = missing+"; "+present;
            unitTester.testDontStop( false, 'msgid "'+id+'" is '+missing+'; '+present );
        }
    }
};
Dyn.locUtil = LocUtil;
DynG.translations = { // make accessible to console as window.DynG.default.translations
    inconsistent: LocUtil.missingMsgIDs,
    missing: LocUtil.missingTranslations,
    untranslated: LocUtil.nullTranslations,
    bothUandLCase: LocUtil.bothUandLCaseMsgIDs,
};

/**
 * String localizer for language translations; localize the given string to the current language,
 * and replace parameters like "{{mean}} with the scope value, e.g. 6.2 for scope={ mean:6.2 }.
 * See http://messageformat.github.io/Jed/ for core translate() function.
 * Note that we are not currently using domain, context, or plurals options to translate().
 * See also TuvaDataTool.switchLanguage(), TuvaDataTool.setLang()
 * @param string - string to translate, including replacement tokens like "hello {{last}}, {{first}}"
 * @param scope - scope for replacement, e.g. { first: "John", last: "Smith" }
 * @returns {*} - same or translated string
 */
Dyn.loc = function( string, scope /*, context*/ ) {
    // defaultLanguage = i18n.defaults.locale_data.messages[""].lang, // expect "en"
    // Replace string with translation if any
	if( Dyn.dataSet?.gettextCatalog ) {
		let lcString = string.toLowerCase(), // both string and key should be LC, see keysToLowerCase()
            i18n = Dyn.dataSet.gettextCatalog,
            trString = i18n.translate(lcString).fetch(), // note: trString===lcString if no translation found
            translated = ( trString !== lcString && trString.length > 0 ),
            nullTranslation = ( trString.length===0 || trString.toLowerCase()===string.toLowerCase());
        if( translated ) {
			string = trString;
		}
        if( Dyn.debugTranslations && nullTranslation ) {
            // translation is empty or unchanged (case insensitive) or not found : record and report why, for debugging
            let langString = i18n.options.locale_data.messages[""].lang, // e.g. "es"
                msgKey = langString+":"+lcString, // e.g. "es:case"
                translationExists = i18n.options.locale_data.messages.hasOwnProperty( lcString );
            if( translationExists ) {
                if( ! Dyn.locUtil.nullTranslations.hasOwnProperty( msgKey ) ) {
                    console.log("Dyn.loc() ignore untranslated", '"'+string+'"',
                        scope ? "Example scope:":'', scope ? scope:'' );
                }
                Dyn.locUtil.nullTranslations[msgKey] = string;
            } else {
                let stringIsTranslationValue = false; //i18n.options.locale_data.messages.includes( string );
                if( ! (Dyn.locUtil.missingTranslations.hasOwnProperty( msgKey ) || stringIsTranslationValue )) {
                    console.log("Dyn.loc() missing translation:", '"'+string+'"', "for", langString+".po",
                        scope ? "Example scope:":'', scope ? scope:'' );
                    //console.log( "msgid "+'"'+lcString+'"' );
                    //console.log( "msgstr "+'"'+string+'"', "NEEDS TRANSLATION");
                    // TODO: Send message to Sentry for easy finding of missing translations
                    // see https://docs.sentry.io/platforms/javascript/usage/
                    // https://docs.sentry.io/platforms/javascript/guides/react/
                    //Sentry.captureMessage("Translation missing for language '"+langString+"' : msgid '"+string+"'");
                }
                Dyn.locUtil.missingTranslations[msgKey] = string;
            }
        }
	}

	// Replace the dynamic properties like "{{count}}" with actual value from scope
	if( typeof scope === "object" ) {
		for( let prop in scope ) {
            if( scope.hasOwnProperty( prop )) {
                //let oldString = string;
                string = string.replace( new RegExp( '{{' + prop + '}}', 'gi' ), scope[prop] );
                //console.log( "Dyn.loc() looking for ", prop, "in '", oldString, "' to make '", string, "'" );
            }
		}
	}

	return string;
};

Dyn.time = function( funcName, state ){
	"use strict";
	if( Dyn.debugging ){
		if( state === "Start" )
			console.time( funcName );
		else
			console.timeEnd( funcName );
	}
};

// Allow inline variable formating in string
Dyn.stringFormat = function ( fmtstr ) {
    var args = Array.prototype.slice.call(arguments, 1);
    return fmtstr.replace(/\{(\d+)\}/g, function (match, index) {
        return args[index];
    });
};

Dyn.speak = function(str) {
    var msg = new SpeechSynthesisUtterance();
    msg.text = '<?xml version="1.0"?>\r\n<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xml:lang="en-US">' + str + '</speak>';
    msg.lang = 'en';
    window.speechSynthesis.speak(msg);
};

Dyn.UndoStep = UndoStep;
Dyn.UndoHelper = UndoHelper;
Dyn.undoManager = new UndoManager( Dyn );

/**
 * utility functions to share with plot to ensure parameter consistency
 * Model Functions
 **/
Dyn.FunctionUtil = {
    // for correct undo/redo, the hidden function IDs should not repeat
    nextFunctionID: 100, // start at 100 to make it clear this is not an array index
    nextColorIndex: 0,

    getNextFunctionID: function() {
        return this.nextFunctionID++; // for next ModelFunctions.props.modelFunctionId
    },
    getNextFunctionColor: function( /*colorArray*/ ) {
        const colorArray = Dyn.plotUtil.functionCurveColors;
        let colorIndex = (this.nextColorIndex++) % (colorArray.length);
        return colorArray[ colorIndex ]; // for next ModelFunctions.state.lineColor
    },
    makeSliderVar: function( name, min, max, val ) {
        return {
            name: name,
            min: min,
                max: max,
                val: val
            };
    },
    getConstantsFromSliderVars: function( state ) {
        let constants = {};
        if( state.sliderVars ) {
            state.sliderVars.forEach( ( slider ) => {
                constants[slider.name] = slider.val;
            } );
        }
        return constants;
    },
    copySliderVar: function( v ) {
        return {
            name: v.name,
            min: v.min,
            max: v.max,
            val: v.val
        };
    },
    copyFunction: function( source, dest = {} ) {
        dest.formulaString = source.formulaString || "";
        dest.lineColor = source.lineColor;
        dest.sliderVars = [];
        dest.rangeX = source.rangeX || "";
        dest.rangeY = source.rangeY || "";
        dest.warningMessage = source.warningMessage || "";
        if( source.sliderVars ) {
            source.sliderVars.forEach( ( v ) => {
                dest.sliderVars.push( this.copySliderVar( v ) );
            });
        }
        if( typeof source.lineVisible==='boolean' ) {
            dest.lineVisible = source.lineVisible;
        }
        return dest;
    },
    hasSavedState: function( id ) {
        return Boolean( Dyn.deFormulas && Dyn.deFormulas[ id ] );
    },
    getSavedState: function( id ) {
        return( Dyn.deFormulas && Dyn.deFormulas[ id ] );
    },
    copyFunctionState: function( id ) {
        var f;
        if( this.hasSavedState( id )) {
            f = this.copyFunction( Dyn.deFormulas[ id ] );
        } else {
            f = {};
        }
        return f;
    },
    copyAllFunctions: function() {
        let all = {};
        for (var key in Dyn.deFormulas){
            all[key] = this.copyFunction( Dyn.deFormulas[key] );
        }
        return all;
    },
    saveFunctionState: function( id, state ) {
        if( ! Dyn.deFormulas ) {
            Dyn.deFormulas = {};
        }
        Dyn.deFormulas[ id ] = this.copyFunction( state );
    },
    deleteFunctionState: function( id ) {
        if( this.hasSavedState( id )) {
            delete Dyn.deFormulas[ id ];
        }
    },
    restoreAllFunctions: function( all ) {
        const colorArray = Dyn.plotUtil.functionCurveColors;
        let oldState = this.copyAllFunctions();
        Dyn.deFormulas = all;
        // make sure nextFunctionID is unique, larger that max of Dyn.deFormulas keys restored from disk.
        // and nextColorIndex should be larger than index of any used standard colors, to avoid repetition
        for (let key in Dyn.deFormulas){
            this.nextFunctionID = Math.max( this.nextFunctionID, parseInt( key ) + 1 );
            let usedColorIndex = colorArray.indexOf( Dyn.deFormulas[key]?.lineColor );
            this.nextColorIndex = Math.max( this.nextColorIndex, usedColorIndex + 1 );
        }
        // notify ModelFunctions panels and Plot that they need updating
        Dyn.mediator.publish( "plot:restoreFormulas", { noUndo:true, oldStateAllFunctions:oldState });
    },
    deleteAllFunctions: function() {
        let oldState = this.copyAllFunctions();
        Dyn.deFormulas = {};
        // notify ModelFunctions panels and Plot that they need updating
        Dyn.mediator.publish( "plot:restoreFormulas", { noUndo:true, noFormulas:true, oldStateAllFunctions:oldState });
    },
    getNumFunctions: function() {
        let n = 0;
        if( Dyn.deFormulas ) {
            for (var key in Dyn.deFormulas) {
                ++n;
            }
        }
        return n;
    },
    changeHintUndoStrings: {
        // map of change hints to undo strings visible to user
        //   See saveOrUpdateLastUndoStep() where undo() of repeated actions for same id and changeHint are done in one step.
        "restore":      "restore", // does not create an undo step, so not visible to user
        "sliderVal":    "Change to Slider for Model {{n}}",
        "color":        "Change to Color for Model {{n}}",
        "mathField":    "Edit Function for Model {{n}}",
        "removeFunc":   "Remove Model Function {{n}}",
        "addFunc":      "Add Model Function {{n}}",
        "lineVisible":  "Show/Hide line for Model {{n}}",
    },
    hasFormulas: function() {
        return !!Dyn.deFormulas && Object.keys(Dyn.deFormulas).length !== 0;
    }
};

// Wrapping other object under new Dyn scope
wrapDragDropManager(Dyn);
wrapDynDataSet(Dyn);
wrapDynCaseFilter(Dyn);
wrapDynCaseSampler(Dyn);
wrapDynAttribute(Dyn);
wrapDynFormula(Dyn);
wrapDynmathUtil(Dyn);
wrapDyndataUtil(Dyn);
wrapDyncolorUtil(Dyn);
wrapDynDataSonification(Dyn);
wrapDynplotUtil(Dyn);
wrapDynPlotLabels(Dyn);
wrapDynTopMarginView(Dyn);
wrapDynRightMarginView(Dyn);
wrapDynaxisUtil(Dyn);
wrapDynAxisView(Dyn);
wrapDynPlotElement(Dyn);
wrapDynMixupElement(Dyn);
wrapDynDotPlotElement(Dyn);
wrapDynBoxPlotElement(Dyn);
wrapDynPieElement(Dyn);
wrapDynBarElement(Dyn);
wrapDynHistoElement(Dyn);
wrapDynPlotShaper(Dyn);
wrapDynPlotMeasures(Dyn);
wrapDynIconStyler(Dyn);
wrapDynaverageElement(Dyn);
wrapDynSpreadElement(Dyn);
wrapDynSquaresOnLineModel(Dyn);
wrapDynRefLineElement(Dyn);
wrapDynLeastSquaresElement(Dyn);
wrapDynPlotFunctionElement(Dyn);
wrapDynAnnotateElement(Dyn);
wrapDynMapElement(Dyn);
wrapDynCountElement(Dyn);
wrapDynGridLinesElement(Dyn);
wrapDynProgressIndicator(Dyn);
wrapDynPlotView(Dyn);
wrapDynToolTip(Dyn);
//wrapDyndragHandler(Dyn);
wrapDynSampleStats(Dyn);
wrapRulerElement(Dyn);

return Dyn;
}

/**
 * DragDropManager
 *
 * Handle dragging from HTML to dropping on SVG
 * TODO: add the method to set and get value
 * on set value publish the message
 * Create the member functions which can be used to
 * publish and subscribe the message
 */

const wrapDragDropManager = function(Dyn) {
	Dyn.DragDropManager = {

	dragged: null,
	droppable: null,
	dropObjects: null,  // TODO: remove if not in use
	targetObject: null, // TODO: remove if not in use
    overObject: null,
    publishStartOnFirstMove: true,

	/**
	 * Subscribe to dragStart from table, caseview or possible
	 * any other source
	 */
	// mediator.subscribe("records:dragStart", this.setDraggedValue);
	// mediator.subscribe("records:dragEnd", this.resetDraggedValue);
	draggedMatchesTarget: function() {
		"use strict";
		// this one gets called when drag is in motion
		// so check the drop targets, if its on any of the drop target

		if (!this.droppable) return false;
		// check if the target match with the drop area.
		return true;
	},

	/**
	 * on drag start from caseCardView or caseTableView
	 * publish the message which can plot is subscribing
	 * on, to highlight
	 */
	setDraggedValue: function( value ){
		"use strict";
		this.dragged = value;
		this.publishStartOnFirstMove = ! Dyn.environment.isMobile();

        if( ! this.publishStartOnFirstMove ) {
            // publish on drag start to highlight the drop areas
            // NOTE: for touch-enabled browsers, touch start is after a long click
            // but before touch-move.  We publish as soon as possible so users
            // know how long to touch before they can start moving (#428)
            Dyn.mediator.publish( "records:dragStart", this.dragged );
        }
	},

    /** record which object the mouse/touch move has entered */
    setOverObject: function( object ) {
        if( Dyn.environment.isMobile() ) {
            this.overObject = object;
        }
    },

    /** handler for mouse or touch move during dragging */
	dragInProgress: function( e, obj, isFirstTime ) {
		"use strict";
        // console.log('DragDropManager.dragInProgress isMobile='+Dyn.environment.isMobile());
        if( isFirstTime && this.publishStartOnFirstMove ) {
            // publish on first drag-move to highlight the drop areas
            // NOTE: do this for non-touch devices to avoid drop-area flicker
            //      when clicking but not moving on drag area (e.g. case card attribute name)
            Dyn.mediator.publish( "records:dragStart", this.dragged );
        }
        if ( Dyn.environment.isMobile() ) {
            // with touch, we must ask other objects to check to see if dragged object is over their drop areas
            Dyn.mediator.publish( "records:dragTouchMove", {
                event: e,
                x: obj.x,
                y: obj.y,
                dragData: this.getDraggedValue()
            });
        }
	},

	getDraggedValue: function(){
		"use strict";
		return this.dragged;
	},

    isInDrag: function() {
	    return( this.dragged !== null );
    },

    /** handler for mouse up or touch end during dragging */
	dragEnd: function( e, obj ){
		"use strict";
		// Check the drag end - drop target location, if its a match.
		// call the specific function.
		// console.log('x: ' + e.target.getBoundingClientRect().left + ', y: '+ e.target.getBoundingClientRect().top);
        // console.log('DragDropManager.dragEnd isMobile='+Dyn.environment.isMobile());

		if ( Dyn.environment.isMobile() ){
            // with touch, we must ask other objects to check to see if dragged object is over their drop areas
            Dyn.mediator.publish( "records:dragTouchEnd", {
                event: e,
                x: obj.x,
                y: obj.y,
                dragData: this.getDraggedValue()
            });
		}
		this.resetDraggedValue();

		// publish the message
		Dyn.mediator.publish( "records:dragEnd", this.dragged );
	},

	/**
	 * once the value is dropped on the plot, publish the message
	 * which reset the dragged object
	 */
	resetDraggedValue: function(){
		"use strict";
		this.dragged = null;
        this.overObject = null;
	}
};
}

/**
 * Extend Function global prototype to allow class inheritance.
 *
 * Code based on http://phrogz.net/js/classes/OOPinJS2.html
 * Note: this.parent.{functionname}.call( this, {args...}) can be used to call parent method.
 * But deep inheritance is not supported so this works for only a single level of override.
 *
 * @param parentClassOrObject {{}} or function()
 * @returns {Function}
 */
// Function.prototype.inheritsFrom = function( parentClassOrObject ){
// 	"use strict";
// 	if ( parentClassOrObject.constructor == Function )
// 	{
// 		//Normal Inheritance
// 		this.prototype = new parentClassOrObject();
// 		this.prototype.constructor = this;
// 		this.prototype.parent = parentClassOrObject.prototype;
// 	}
// 	else
// 	{
// 		//Pure Virtual Inheritance
// 		this.prototype = parentClassOrObject;
// 		this.prototype.constructor = this;
// 		this.prototype.parent = parentClassOrObject;
// 	}
// 	return this;
// };

DynG.initDyn = initDyn
